import { SalaryTable, weeklyGross } from "@/components/admin/SalaryTable";
import { SalaryEditDialog } from "@/components/admin/SalaryEditDialog";
import { ArchiveEmployeeDialog } from "@/components/admin/ArchiveEmployeeDialog";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, apiErrorMessage } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Users, EnvelopeSimple, Bell, Plus, Trash, Copy, ArrowClockwise,
  ShieldCheck, ShieldStar, Eye, CheckCircle, PlugsConnected, Receipt, PushPin, Clock, ActivityIcon as Activity, CurrencyDollar, Lock, LockOpen, UserSwitch, ChatCircleDots, Key, Envelope, Sun, Moon, CircleNotch, UserPlus, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { AddEmployeeDialog } from "@/components/admin/AddEmployeeDialog";
import { toast } from "sonner";
import NotesPanel from "@/components/NotesPanel";
import { SalaryNotesPanel } from "@/components/admin/SalaryNotesPanel";
import { useTheme } from "@/context/ThemeContext";
import { SoftwareConnectionsPanel } from "@/components/admin/software/SoftwareConnectionsPanel";
import { ReceiptsPanel } from "@/components/admin/software/ReceiptsPanel";
import { NavBarSettings } from "@/components/admin/NavBarSettings";

const ROLE_OPTIONS = [
  { value: "view_only", label: "View-only user", icon: Eye },
  { value: "secondary_admin", label: "Employee user", icon: ShieldCheck },
  { value: "admin", label: "Management user", icon: ShieldCheck },
  { value: "super_admin", label: "Super admin", icon: ShieldStar },
];

const ROLE_LABELS = {
  admin: "Management user",
  secondary_admin: "Employee user",
  view_only: "View-only user",
  super_admin: "Super admin",
};

const ROLE_BADGE = {
  admin: "bg-[#327de3]/10 text-[#1f64c2] border-[#327de3]/30",
  secondary_admin: "bg-violet-50 text-violet-700 border-violet-200",
  view_only: "bg-slate-100 text-slate-600 border-slate-200",
  super_admin: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function AdminPage() {
  const { user } = useAuth();
  const isSuper = !!user?.is_super_admin;
  const isAdmin = user?.role === "admin";

  // Notes are visible to ANY user reaching /admin (admin / secondary / super).
  // For non-super admins, Notes is now the ONLY tab. Notifications + Activity
  // are gated to super admin alongside Users / Invites / Salaries.
  const [tab, setTab] = useState(isSuper ? "users" : "notes");
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [notesUnread, setNotesUnread] = useState(0);

  const loadAll = async () => {
    try {
      const promises = [api.get("/businesses")];
      if (isSuper) {
        promises.unshift(
          api.get("/admin/users"),
          api.get("/admin/invites"),
          api.get("/admin/notifications"),
        );
      }
      const results = await Promise.all(promises);
      if (isSuper) {
        setUsers(results[0].data);
        setInvites(results[1].data);
        setNotifications(results[2].data);
        setBusinesses(results[3].data);
      } else {
        setBusinesses(results[0].data);
      }
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    }
  };

  const loadNotesUnread = async () => {
    try {
      const { data } = await api.get("/notes/unread-count");
      setNotesUnread(data?.count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadAll(); loadNotesUnread(); /* eslint-disable-next-line */ }, [isSuper]);

  // If a non-super admin somehow lands on a gated tab (state carry-over),
  // bounce them to a tab they can see.
  useEffect(() => {
    if (!isSuper && tab !== "notes") {
      setTab("notes");
    }
  }, [isSuper, tab]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const pendingInvites = invites.filter((i) => i.status === "pending").length;

  return (
    <div className="min-h-screen app-bg">
      <AppHeader />

      <main className={`${tab === "salaries" ? "max-w-none 2xl:px-12" : "max-w-[1280px]"} mx-auto px-4 sm:px-6 lg:px-8 py-8`} data-testid="admin-main">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Admin</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage users, send invitations, and review who has accepted them.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-6 flex-wrap">
          <ThemeTogglePill />
          {isSuper && (
            <TabButton active={tab === "users"} onClick={() => setTab("users")} icon={<Users size={16} weight="bold" />} testId="admin-tab-users">
              Users <span className="ml-1.5 text-xs text-slate-400">{users.length}</span>
            </TabButton>
          )}
          {isSuper && (
            <TabButton active={tab === "invites"} onClick={() => setTab("invites")} icon={<EnvelopeSimple size={16} weight="bold" />} testId="admin-tab-invites">
              Invites {pendingInvites > 0 && <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5">{pendingInvites}</span>}
            </TabButton>
          )}
          <TabButton active={tab === "notes"} onClick={() => setTab("notes")} icon={<ChatCircleDots size={16} weight="bold" />} testId="admin-tab-notes">
            Notes
            {notesUnread > 0 && (
              <span className="ml-1.5 text-[10px] bg-emerald-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] inline-flex items-center justify-center font-semibold" data-testid="admin-tab-notes-pill">
                {notesUnread}
              </span>
            )}
          </TabButton>
          {isSuper && (
            <TabButton active={tab === "notifications"} onClick={() => setTab("notifications")} icon={<Bell size={16} weight="bold" />} testId="admin-tab-notifications">
              Notifications {unreadCount > 0 && <span className="ml-1.5 text-[10px] bg-[#D92D20] text-white rounded-full px-1.5 py-0.5">{unreadCount}</span>}
            </TabButton>
          )}
          {isSuper && (
            <TabButton active={tab === "activity"} onClick={() => setTab("activity")} icon={<Activity size={16} weight="bold" />} testId="admin-tab-activity">
              Log
            </TabButton>
          )}
          {isSuper && (
            <TabButton active={tab === "salaries"} onClick={() => setTab("salaries")} icon={<CurrencyDollar size={16} weight="bold" />} testId="admin-tab-salaries">
              Salaries
            </TabButton>
          )}
          {(isSuper || isAdmin) && (
            <TabButton active={tab === "software"} onClick={() => setTab("software")} icon={<PlugsConnected size={16} weight="bold" />} testId="admin-tab-software-connections">
              Software Connections
            </TabButton>
          )}
          {(isSuper || isAdmin) && (
            <TabButton active={tab === "receipts"} onClick={() => setTab("receipts")} icon={<Receipt size={16} weight="bold" />} testId="admin-tab-emergent-receipts">
              Emergent Receipts
            </TabButton>
          )}
          {isSuper && (
            <TabButton active={tab === "navbar"} onClick={() => setTab("navbar")} icon={<PushPin size={16} weight="bold" />} testId="admin-tab-navbar">
              Nav Bar
            </TabButton>
          )}
        </div>

        {tab === "notes" && (
          <NotesPanel refreshHeader={loadNotesUnread} />
        )}

        {tab === "users" && isSuper && (
          <UsersPanel
            users={users}
            businesses={businesses}
            currentUser={user}
            isSuper={isSuper}
            isAdmin={isAdmin}
            reload={loadAll}
          />
        )}

        {tab === "invites" && isSuper && (
          <InvitesPanel
            invites={invites}
            businesses={businesses}
            isSuper={isSuper}
            reload={loadAll}
          />
        )}

        {tab === "notifications" && isSuper && (
          <NotificationsPanel
            notifications={notifications}
            reload={loadAll}
          />
        )}

        {tab === "activity" && isSuper && (
          <ActivityPanel businesses={businesses} />
        )}

        {tab === "salaries" && isSuper && (
          <SalariesPanel />
        )}

        {tab === "software" && (isSuper || isAdmin) && <SoftwareConnectionsPanel />}
        {tab === "receipts" && (isSuper || isAdmin) && <ReceiptsPanel />}
        {tab === "navbar" && isSuper && <NavBarSettings />}
      </main>
    </div>
  );
}

function TabButton({ active, onClick, icon, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition-colors ${
        active
          ? "border-[#327de3] text-[#1f64c2]"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

/* =================== THEME TOGGLE PILL ===================
 * Bloomberg-style dark/light switcher — segmented pill, lives
 * immediately left of the Users tab. Persisted via localStorage
 * through ThemeContext. */
function ThemeTogglePill() {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  return (
    <div
      className="mr-3 inline-flex items-center rounded-full border border-slate-200 bg-white p-0.5 shadow-sm"
      data-testid="admin-theme-toggle"
      role="group"
      aria-label="Appearance"
    >
      <button
        type="button"
        onClick={() => setTheme("light")}
        aria-pressed={!isDark}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors ${
          !isDark ? "bg-[#327de3] text-white shadow" : "text-slate-500 hover:text-slate-700"
        }`}
        data-testid="admin-theme-light"
        title="Switch to Light mode"
      >
        <Sun size={12} weight="bold" /> Light
      </button>
      <button
        type="button"
        onClick={() => setTheme("dark")}
        aria-pressed={isDark}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors ${
          isDark ? "bg-[#141821] text-amber-300 shadow" : "text-slate-500 hover:text-slate-700"
        }`}
        data-testid="admin-theme-dark"
        title="Switch to Dark mode (Bloomberg)"
      >
        <Moon size={12} weight="bold" /> Dark
      </button>
    </div>
  );
}

/* =================== USERS PANEL =================== */
function UsersPanel({ users, businesses, currentUser, isSuper, isAdmin, reload }) {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(null); // user object being edited
  const [form, setForm] = useState({ role: "view_only", business_ids: [], permissions: {} });
  const [saving, setSaving] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      // "super_admin" is a virtual role used by the UI only; internally that's
      // role=admin + is_super_admin=true.
      role: u.is_super_admin ? "super_admin" : (u.role || "view_only"),
      business_ids: u.viewable_business_ids || [],
      permissions: { ...(u.effective_permissions || {}) },
    });
  };

  const togglePerm = (key) => {
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  };

  const toggleBiz = (id) => {
    setForm((f) => ({
      ...f,
      business_ids: f.business_ids.includes(id)
        ? f.business_ids.filter((x) => x !== id)
        : [...f.business_ids, id],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Map the virtual "super_admin" role to role=admin + is_super_admin=true.
      // Sending is_super_admin=false alongside a real role demotes existing
      // super admins.
      if (payload.role === "super_admin") {
        payload.role = "admin";
        payload.is_super_admin = true;
      } else if (editing?.is_super_admin) {
        payload.is_super_admin = false;
      }
      await api.put(`/admin/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditing(null);
      reload();
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    } finally {
      setSaving(false);
    }
  };

  const doImpersonate = async (u) => {
    setImpersonatingId(u.id);
    try {
      await impersonate(u.id);
      toast.success(`Viewing as ${u.name || u.email}`);
      navigate("/dashboard");
    } catch (e) {
      const m = apiErrorMessage(e);
      if (m) toast.error(m);
    } finally {
      setImpersonatingId(null);
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      reload();
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>User</Th>
              <Th>Role</Th>
              <Th className="hidden md:table-cell">Business access</Th>
              <Th className="hidden sm:table-cell">Joined</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isYou = u.id === currentUser?.id;
              const isTargetAdmin = u.role === "admin";
              const canEditThisUser = isSuper || !isTargetAdmin; // secondary admins can't edit admins
              const canDeleteThisUser =
                isAdmin && !isYou && !u.is_super_admin; // only admins delete, not self, not super
              const businessCount = isTargetAdmin
                ? "All (admin)"
                : `${(u.viewable_business_ids || []).length} business${(u.viewable_business_ids || []).length === 1 ? "" : "es"}`;
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                  <Td>
                    <div className="font-medium text-slate-900">
                      {u.name || u.email.split("@")[0]}
                      {u.is_super_admin && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#327de3]">Super</span>}
                      {isYou && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">You</span>}
                    </div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </Td>
                  <Td>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-semibold ${u.is_super_admin ? ROLE_BADGE.super_admin : (ROLE_BADGE[u.role] || ROLE_BADGE.view_only)}`}>
                      {u.is_super_admin ? "Super admin" : (ROLE_LABELS[u.role] || u.role)}
                    </span>
                  </Td>
                  <Td className="hidden md:table-cell text-xs text-slate-600">{businessCount}</Td>
                  <Td className="hidden sm:table-cell text-xs text-slate-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </Td>
                  <Td className="text-right">
                    <div className="inline-flex items-center gap-2 justify-end flex-nowrap whitespace-nowrap" data-testid={`user-actions-${u.id}`}>
                      {isSuper && !isYou && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => doImpersonate(u)}
                          disabled={impersonatingId === u.id}
                          data-testid={`user-impersonate-${u.id}`}
                          title="View the app as this user"
                        >
                          <UserSwitch size={14} className="mr-1" />
                          {impersonatingId === u.id ? "Loading…" : "View as"}
                        </Button>
                      )}
                      {isSuper && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setResetTarget(u)}
                          data-testid={`user-reset-pw-${u.id}`}
                          title="Show / reset password"
                        >
                          <Key size={14} className="mr-1" />
                          Reset password
                        </Button>
                      )}
                      {canEditThisUser && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(u)}
                          data-testid={`user-edit-${u.id}`}
                        >
                          Edit
                        </Button>
                      )}
                      {canDeleteThisUser && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[#D92D20] hover:text-[#D92D20] hover:bg-red-50"
                              data-testid={`user-delete-${u.id}`}
                            >
                              <Trash size={14} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this user?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove <strong>{u.name || u.email}</strong> from Strata.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => remove(u)}
                                className="bg-[#D92D20] hover:bg-red-700"
                                data-testid={`user-delete-confirm-${u.id}`}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit user dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Edit {editing?.name || editing?.email}
            </DialogTitle>
            <DialogDescription>
              {ROLE_LABELS[editing?.role] || editing?.role}. Adjust role and business access below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm((f) => {
                  // When role drops to a level that cannot hold AI / salaries-button
                  // access, visibly clear those perms so the checkboxes mirror the
                  // server-side rule (otherwise they stay checked + greyed which is
                  // confusing — see iteration_8 frontend feedback).
                  const next = { ...f, role: v };
                  if (v === "secondary_admin" || v === "view_only") {
                    next.permissions = { ...f.permissions, timesheet_ai: false, salaries_button: false };
                  }
                  if (v === "view_only") {
                    next.permissions = { ...next.permissions, payroll_access: false };
                  }
                  return next;
                })}
                disabled={editing?.id === currentUser?.id}
              >
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      disabled={!isSuper && (r.value === "admin" || r.value === "secondary_admin" || r.value === "super_admin")}
                    >
                      {r.label}
                      {!isSuper && (r.value === "admin" || r.value === "secondary_admin" || r.value === "super_admin") && (
                        <span className="ml-2 text-xs text-slate-400">(super admin only)</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editing?.id === currentUser?.id && (
                <p className="text-[11px] text-slate-500">You cannot change your own role.</p>
              )}
            </div>

            {(form.role === "view_only" || form.role === "secondary_admin") && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Businesses
                </Label>
                <p className="text-xs text-slate-500">
                  Check each business this user should be able to access. They&apos;ll see them on both the
                  <strong> Businesses</strong> page and the <strong>Dashboard</strong>.
                </p>
                <div className="border rounded-lg p-3 max-h-56 overflow-y-auto space-y-2 ml-4" data-testid="user-businesses-checkboxes">
                  {businesses.length === 0 ? (
                    <div className="text-xs text-slate-400">No businesses yet — add one first.</div>
                  ) : businesses.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={form.business_ids.includes(b.id)}
                        onCheckedChange={() => toggleBiz(b.id)}
                        data-testid={`user-business-checkbox-${b.id}`}
                      />
                      <span className="text-sm text-slate-700">{b.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {(form.role === "admin" || form.role === "super_admin") && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                {form.role === "super_admin" ? (
                  <>
                    Super admins automatically see <strong>all businesses</strong> and can manage users,
                    invites, salaries, notifications, log entries, and impersonate other accounts.
                  </>
                ) : (
                  <>
                    Management users automatically see <strong>all businesses</strong>. Per-business
                    permissions only apply to view-only and employee users.
                  </>
                )}
              </div>
            )}

            {/* Feature permissions — super admin only */}
            {isSuper && (
              <div className="space-y-2 border-t border-slate-200 pt-4">
                <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Feature permissions</Label>
                <p className="text-xs text-slate-500">
                  Toggle access to specific Strata features. Permissions never grant business-level data.
                </p>
                <div className="space-y-2">
                  <PermToggle
                    label="Timesheet access"
                    desc="Show the Timesheet page in the nav bar."
                    checked={!!form.permissions.timesheet_access}
                    onChange={() => togglePerm("timesheet_access")}
                    disabled={editing?.is_super_admin}
                    testId="perm-timesheet-access"
                  />
                  {/* Indented sub-feature of Timesheet access — by default
                      restricted to super admins. */}
                  <div className="ml-7 pl-3 border-l-2 border-slate-200">
                    <PermToggle
                      label='Allow view of "Add salaries" button on timesheet'
                      desc="Lets this user open the Salaries editor from the Timesheet header."
                      checked={!!form.permissions.salaries_button}
                      onChange={() => togglePerm("salaries_button")}
                      disabled={
                        editing?.is_super_admin ||
                        !form.permissions.timesheet_access ||
                        form.role === "secondary_admin" ||
                        form.role === "view_only"
                      }
                      note="By default not permissioned to secondary admins, only super admins."
                      testId="perm-salaries-button"
                    />
                  </div>
                  <PermToggle
                    label="AI Hour Search"
                    desc="Natural-language Q&amp;A over timesheet rows."
                    checked={!!form.permissions.timesheet_ai}
                    onChange={() => togglePerm("timesheet_ai")}
                    disabled={
                      editing?.is_super_admin ||
                      form.role === "view_only" ||
                      form.role === "secondary_admin"
                    }
                    note={
                      form.role === "secondary_admin"
                        ? "Locked off for employee users — prevents using AI to look up other users' salaries."
                        : (form.role === "view_only" ? "Not grantable to view-only users." : "")
                    }
                    testId="perm-timesheet-ai"
                  />
                  {/* Payroll access is now always-on by default for management
                      and employee users and is no longer exposed as a toggle. */}
                </div>

                {/* Notes access — general + permissioned (super admin only
                    grant). Permissioned notes are the tier used for
                    passwords / sensitive data — deliberately gated. */}
                <div className="space-y-2 mt-4 pt-4 border-t border-slate-100">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Notes</Label>
                  <PermToggle
                    label="Notes"
                    desc="Show the Admin → Notes feed and let this user post."
                    checked={!!form.permissions.notes_general_access}
                    onChange={() => togglePerm("notes_general_access")}
                    disabled={editing?.is_super_admin}
                    testId="perm-notes-general"
                  />
                  <div className="ml-7 pl-3 border-l-2 border-slate-200">
                    <PermToggle
                      label="Permissioned Notes"
                      desc="Allow viewing / posting notes in the Permissioned tier."
                      checked={!!form.permissions.notes_permissioned_access}
                      onChange={() => togglePerm("notes_permissioned_access")}
                      disabled={
                        editing?.is_super_admin ||
                        !form.permissions.notes_general_access
                      }
                      note="This allows users to have access to permissioned notes like passwords"
                      noteTone="danger"
                      testId="perm-notes-permissioned"
                    />
                  </div>
                </div>

                {/* Loans page access — management-only feature, gated per user. */}
                <div className="space-y-2 mt-4 pt-4 border-t border-slate-100">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">loans/assets</Label>
                  <PermToggle
                    label="loans/assets page"
                    desc="Show the loans/assets page (KPIs, table, per-asset detail modal with attachments)."
                    checked={!!form.permissions.loans_access}
                    onChange={() => togglePerm("loans_access")}
                    disabled={editing?.is_super_admin || form.role === "view_only"}
                    note={form.role === "view_only" ? "Not grantable to view-only users." : ""}
                    testId="perm-loans-access"
                  />
                </div>
                {/* Insurance page access — enabled by default for every role;
                    super admin can disable for individual users. */}
                <div className="space-y-2 mt-4 pt-4 border-t border-slate-100">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Reminders</Label>
                  <PermToggle
                    label="Insurance page"
                    desc="Show the Insurance page under Reminders (KPIs, table, per-policy detail with attachments)."
                    checked={form.permissions.insurance_access !== false}
                    onChange={() => togglePerm("insurance_access")}
                    disabled={editing?.is_super_admin}
                    testId="perm-insurance-access"
                  />
                  <PermToggle
                    label="Subcontractor Insurance page"
                    desc="Show the Subcontractor Insurance page under Reminders."
                    checked={form.permissions.subcontractor_access !== false}
                    onChange={() => togglePerm("subcontractor_access")}
                    disabled={editing?.is_super_admin}
                    testId="perm-subcontractor-access"
                  />
                  <PermToggle
                    label="EIN page"
                    desc="Show the EIN page under Reminders (entity table + biennial expiration reminders)."
                    checked={form.permissions.ein_access !== false}
                    onChange={() => togglePerm("ein_access")}
                    disabled={editing?.is_super_admin}
                    testId="perm-ein-access"
                  />
                </div>
                {/* Timesheet management (Raw Data) — default OFF; enforced server-side. */}
                <div className="space-y-2 mt-4 pt-4 border-t border-slate-100" data-testid="perm-ts-mgmt-group">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Timesheet management</Label>
                  <PermToggle
                    label="Dashboard | Raw Data selector"
                    desc="Show the Raw Data view on the Timesheet page."
                    checked={!!form.permissions.timesheet_management_access}
                    onChange={() => togglePerm("timesheet_management_access")}
                    disabled={editing?.is_super_admin}
                    testId="perm-ts-mgmt-access"
                  />
                  <div className="ml-7 pl-3 border-l-2 border-slate-200 space-y-2">
                    {[
                      ["timesheet_raw_view", "View raw submissions", "See every employee's raw timesheet entries and export them."],
                      ["timesheet_raw_edit", "Correct submissions", "Edit or void entries and see the correction history."],
                      ["timesheet_form_manage", "Manage the form", "Edit questions, preview and publish new form versions."],
                      ["timesheet_jobs_manage", "Manage jobs / properties", "Add, rename, hide or reorder jobsite choices."],
                      ["timesheet_sms_manage", "Manage SMS reminders", "Edit mobile numbers, the reminder schedule and send reminders."],
                    ].map(([k, label, desc]) => (
                      <PermToggle
                        key={k}
                        label={label}
                        desc={desc}
                        checked={!!form.permissions[k]}
                        onChange={() => togglePerm(k)}
                        disabled={editing?.is_super_admin || !form.permissions.timesheet_management_access}
                        testId={`perm-${k.replace(/_/g, "-")}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
              data-testid="user-edit-save"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResetPasswordModal target={resetTarget} onClose={() => setResetTarget(null)} />
    </div>
  );
}

/* =================== RESET PASSWORD MODAL (super admin only) =================== */
function ResetPasswordModal({ target, onClose }) {
  const [mode, setMode] = useState("menu"); // 'menu' | 'set' | 'done'
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sentEmail, setSentEmail] = useState(false);
  const [savedPlaintext, setSavedPlaintext] = useState("");

  useEffect(() => {
    if (target) {
      setMode("menu");
      setPw1(""); setPw2(""); setShowPw(false);
      setBusy(false); setSentEmail(false); setSavedPlaintext("");
    }
  }, [target]);

  if (!target) return null;

  const setPassword = async () => {
    if (pw1.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/reset-password`, { new_password: pw1 });
      setSavedPlaintext(pw1);
      setSentEmail(!!data?.email_sent);
      setMode("done");
      toast.success("Password updated");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setBusy(false); }
  };

  const sendResetLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/send-reset-link`);
      setSentEmail(!!data?.email_sent);
      toast.success(data?.email_sent ? "Reset link emailed" : "Reset link generated (email send failed)");
      onClose();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-pw-modal">
        <DialogHeader>
          <DialogTitle>Reset password — {target.name || target.email}</DialogTitle>
        </DialogHeader>

        {mode === "menu" && (
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              For security, the existing password is stored as a one-way hash and cannot be displayed.
              You can either set a new password directly, or send the user a self-service reset link by email.
            </div>
            <Button
              className="w-full justify-start bg-[#327de3] hover:bg-[#1f64c2] text-white"
              onClick={() => setMode("set")}
              data-testid="reset-pw-choose-set"
            >
              <Lock size={14} className="mr-2" /> Set a new password manually
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={sendResetLink}
              disabled={busy}
              data-testid="reset-pw-send-link"
            >
              <Envelope size={14} className="mr-2" />
              {busy ? "Sending…" : "Send password-reset link to user's email"}
            </Button>
          </div>
        )}

        {mode === "set" && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">New password</Label>
              <Input
                type={showPw ? "text" : "password"}
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                autoFocus
                data-testid="reset-pw-input-1"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Confirm new password</Label>
              <Input
                type={showPw ? "text" : "password"}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                data-testid="reset-pw-input-2"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <Checkbox checked={showPw} onCheckedChange={(v) => setShowPw(!!v)} />
              Show passwords
            </label>
            <p className="text-[11px] text-slate-500">
              The user will be emailed: <em>&ldquo;your password has been changed; if it was not changed by you, contact your system administrator.&rdquo;</em>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode("menu")} disabled={busy}>Back</Button>
              <Button
                onClick={setPassword}
                disabled={busy || !pw1 || !pw2}
                className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
                data-testid="reset-pw-save"
              >
                {busy ? "Saving…" : "Reset password"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {mode === "done" && (
          <div className="space-y-3" data-testid="reset-pw-done">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
              <strong>Password updated.</strong> {sentEmail ? "The user has been notified by email." : "Notification email could not be sent (Resend not configured)."}
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">New password (shown once)</div>
              <code className="block break-all text-sm font-mono bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5" data-testid="reset-pw-plaintext">
                {savedPlaintext}
              </code>
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-[11px] text-slate-500">Copy and share securely — you won&apos;t see this again.</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { navigator.clipboard.writeText(savedPlaintext); toast.success("Copied"); }}
                  data-testid="reset-pw-copy"
                >
                  <Copy size={12} className="mr-1.5" /> Copy
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={onClose} className="bg-[#327de3] hover:bg-[#1f64c2] text-white">Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* =================== INVITES PANEL =================== */
function InvitesPanel({ invites, businesses, isSuper, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "view_only",
    business_ids: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [newLink, setNewLink] = useState(null);

  const toggleBiz = (id) => {
    setForm((f) => ({
      ...f,
      business_ids: f.business_ids.includes(id)
        ? f.business_ids.filter((x) => x !== id)
        : [...f.business_ids, id],
    }));
  };

  const reset = () => {
    setForm({ email: "", name: "", role: "view_only", business_ids: [] });
    setNewLink(null);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/invites", form);
      setNewLink({ url: data.accept_url, emailSent: data.email_sent });
      if (data.email_sent) {
        toast.success(`Invite emailed to ${data.email}`);
      } else {
        toast.message("Invite created — copy the link below to share it manually");
      }
      reload();
    } catch (err) {
      { const _m = apiErrorMessage(err); if (_m) toast.error(_m); }
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/admin/invites/${id}`);
      toast.success("Invite revoked");
      reload();
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    }
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/admin/invites/${id}/resend`);
      if (data.email_sent) toast.success("Invite re-sent by email");
      else toast.message("Email service not configured — link copied below");
      reload();
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {invites.length} invite{invites.length === 1 ? "" : "s"} total
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <Button
            onClick={() => setOpen(true)}
            className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
            data-testid="invite-new-button"
          >
            <Plus size={16} className="mr-1.5" /> Invite user
          </Button>
          <DialogContent className="sm:max-w-lg" data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Invite a new user</DialogTitle>
              <DialogDescription>
                We'll email them a one-time link to set their password and join Strata.
              </DialogDescription>
            </DialogHeader>

            {!newLink ? (
              <form onSubmit={submit} className="space-y-4 mt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="name@example.com"
                    data-testid="invite-email-input"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Display name (optional)</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name"
                    data-testid="invite-name-input-form"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem
                          key={r.value}
                          value={r.value}
                          disabled={!isSuper && (r.value === "admin" || r.value === "secondary_admin")}
                        >
                          {r.label}
                          {!isSuper && (r.value === "admin" || r.value === "secondary_admin") && (
                            <span className="ml-2 text-xs text-slate-400">(super admin only)</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {form.role === "view_only" && (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                      Business access
                    </Label>
                    <div className="border rounded-lg p-3 max-h-44 overflow-y-auto space-y-2">
                      {businesses.length === 0 ? (
                        <div className="text-xs text-slate-400">No businesses yet — add one first.</div>
                      ) : businesses.map((b) => (
                        <label key={b.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={form.business_ids.includes(b.id)}
                            onCheckedChange={() => toggleBiz(b.id)}
                            data-testid={`invite-business-checkbox-${b.id}`}
                          />
                          <span className="text-sm text-slate-700">{b.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <DialogFooter className="mt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
                    data-testid="invite-submit-button"
                  >
                    {submitting ? "Sending…" : "Send invite"}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-4 mt-2" data-testid="invite-success">
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle size={18} weight="fill" /> Invite created
                </div>
                <div className="text-sm text-slate-600">
                  {newLink.emailSent ? (
                    <>Email delivered. They'll see it in their inbox shortly.</>
                  ) : (
                    <>Email service isn't configured yet. Copy the invite link below and share it manually:</>
                  )}
                </div>
                <div className="flex items-stretch gap-2">
                  <Input value={newLink.url} readOnly className="font-mono text-xs" data-testid="invite-success-link" />
                  <Button onClick={() => copyLink(newLink.url)} variant="outline" data-testid="invite-success-copy">
                    <Copy size={14} className="mr-1.5" /> Copy
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={reset} data-testid="invite-success-new">Send another</Button>
                  <Button onClick={() => setOpen(false)} className="bg-[#327de3] hover:bg-[#1f64c2] text-white">Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invites-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th className="hidden sm:table-cell">Status</Th>
                <Th className="hidden md:table-cell">Sent</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {invites.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-slate-400">No invites yet.</td></tr>
              ) : invites.map((i) => (
                <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <Td>
                    <div className="font-medium text-slate-900">{i.email}</div>
                    {i.name && <div className="text-xs text-slate-500">{i.name}</div>}
                  </Td>
                  <Td>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-semibold ${ROLE_BADGE[i.role] || ROLE_BADGE.view_only}`}>
                      {ROLE_LABELS[i.role] || i.role}
                    </span>
                  </Td>
                  <Td className="hidden sm:table-cell">
                    <StatusPill status={i.status} emailSent={i.email_sent} />
                  </Td>
                  <Td className="hidden md:table-cell text-xs text-slate-500">
                    {i.created_at ? new Date(i.created_at).toLocaleDateString() : "—"}
                  </Td>
                  <Td className="text-right">
                    {i.status === "pending" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => copyLink(i.accept_url)} title="Copy link" data-testid={`invite-copy-${i.id}`}>
                          <Copy size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => resend(i.id)} title="Resend email" data-testid={`invite-resend-${i.id}`}>
                          <ArrowClockwise size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#D92D20] hover:text-[#D92D20] hover:bg-red-50"
                          onClick={() => revoke(i.id)}
                          title="Revoke"
                          data-testid={`invite-revoke-${i.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, emailSent }) {
  if (status === "accepted") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
        <CheckCircle size={14} weight="fill" /> Accepted
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
      <Clock size={14} weight="bold" /> Pending {!emailSent && <span className="text-slate-400">(no email)</span>}
    </span>
  );
}

/* =================== NOTIFICATIONS PANEL =================== */
function NotificationsPanel({ notifications, reload }) {
  const unread = useMemo(() => notifications.filter((n) => !n.is_read), [notifications]);

  const markAllRead = async () => {
    try {
      await api.post("/admin/notifications/read-all");
      reload();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const markOne = async (id) => {
    try {
      await api.post(`/admin/notifications/${id}/read`);
      reload();
    } catch (e) { /* noop */ }
  };

  return (
    <div className="space-y-3">
      {unread.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={markAllRead} data-testid="notifications-mark-all-read">
            Mark all read
          </Button>
        </div>
      )}
      {notifications.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="notifications-empty">
          <Bell size={28} className="text-slate-300 mx-auto" />
          <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">No notifications yet</h3>
          <p className="mt-1 text-sm text-slate-500">
            When invited users accept their invitation, you'll see it here.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {notifications.map((n) => (
            <li
              key={n.id}
              className={`bg-white rounded-xl border p-4 flex items-start gap-3 cursor-pointer hover:bg-slate-50/50 ${
                n.is_read ? "border-slate-200" : "border-[#327de3]/40 bg-[#327de3]/[0.03]"
              }`}
              onClick={() => !n.is_read && markOne(n.id)}
              data-testid={`notification-${n.id}`}
            >
              <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${n.is_read ? "bg-slate-100 text-slate-500" : "bg-[#327de3]/10 text-[#327de3]"}`}>
                <CheckCircle size={18} weight="bold" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-slate-900 text-sm">{n.title}</div>
                  {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#327de3]" />}
                </div>
                <div className="text-sm text-slate-600 mt-0.5">{n.message}</div>
                <div className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* =================== ACTIVITY PANEL =================== */
const VIEW_LABELS = {
  summary: "Project Summary",
  pnl: "PNL",
  balance: "Balance Sheet",
  salary_update: "Salary update",
  ai_search: "AI Hour Search",
  atlas_task_create: "Atlas · Task add",
  atlas_task_update: "Atlas · Task edit",
  atlas_task_move: "Atlas · Task move",
  atlas_task_delete: "Atlas · Task trash",
  atlas_task_restore: "Atlas · Task restore",
  atlas_column_rename: "Atlas · Rename column",
  atlas_group_rename: "Atlas · Rename group",
  atlas_layout_update: "Atlas · Reorder panels",
};

const VIEW_PILL = {
  summary: "bg-[#327de3]/10 text-[#1f64c2]",
  pnl: "bg-emerald-50 text-emerald-700",
  balance: "bg-amber-50 text-amber-700",
  salary_update: "bg-rose-50 text-rose-700",
  ai_search: "bg-violet-50 text-violet-700",
  atlas_task_create: "bg-sky-50 text-sky-700",
  atlas_task_update: "bg-sky-50 text-sky-700",
  atlas_task_move: "bg-sky-50 text-sky-700",
  atlas_task_delete: "bg-rose-50 text-rose-700",
  atlas_task_restore: "bg-emerald-50 text-emerald-700",
  atlas_column_rename: "bg-slate-100 text-slate-700",
  atlas_group_rename: "bg-slate-100 text-slate-700",
  atlas_layout_update: "bg-slate-100 text-slate-700",
};

const FEATURE_FILTERS = [
  { value: "all", label: "All features" },
  { value: "project_summary", label: "Project Summary" },
  { value: "timesheet", label: "Timesheet" },
  { value: "atlas", label: "Atlas" },
  { value: "notes", label: "Notes" },
  { value: "unit_documents", label: "Unit documents" },
];

function formatRelativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function ActivityPanel({ businesses }) {
  const [logs, setLogs] = useState([]);
  const [bizFilter, setBizFilter] = useState("all");
  const [featureFilter, setFeatureFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (bizFilter !== "all") params.business_id = bizFilter;
      if (featureFilter !== "all") params.feature = featureFilter;
      const { data } = await api.get("/admin/activity", { params });
      setLogs(data);
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bizFilter, featureFilter]);

  return (
    <div className="space-y-4" data-testid="activity-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="text-sm text-slate-500">
          {loading ? "Loading…" : `${logs.length} most recent log entr${logs.length === 1 ? "y" : "ies"}`}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={featureFilter} onValueChange={setFeatureFilter}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="activity-feature-filter">
              <SelectValue placeholder="Feature" />
            </SelectTrigger>
            <SelectContent>
              {FEATURE_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={bizFilter} onValueChange={setBizFilter}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="activity-business-filter">
              <SelectValue placeholder="All businesses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All businesses</SelectItem>
              {businesses.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" size="sm" data-testid="activity-refresh">
            <ArrowClockwise size={14} />
          </Button>
        </div>
      </div>

      {logs.length === 0 && !loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="activity-empty">
          <Activity size={28} className="text-slate-300 mx-auto" />
          <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">No log entries yet</h3>
          <p className="mt-1 text-sm text-slate-500">When users view a business dashboard or run an AI search, you&apos;ll see it here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="activity-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>User</Th>
                  <Th className="hidden sm:table-cell">Business</Th>
                  <Th>View</Th>
                  <Th className="text-right">When</Th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`activity-row-${l.id}`}>
                    <Td>
                      <div className="font-medium text-slate-900">{l.user_name || l.user_email || "Unknown"}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[220px]">{l.user_email}</div>
                      <div className="text-xs text-slate-500 sm:hidden mt-0.5">{l.business_name}</div>
                    </Td>
                    <Td className="hidden sm:table-cell text-slate-700">{l.view === "salary_update" ? (l.employee || "—") : (l.business_name || "—")}</Td>
                    <Td>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${VIEW_PILL[l.view] || "bg-slate-100 text-slate-600"}`}>
                        {VIEW_LABELS[l.view] || l.view}
                      </span>
                      {l.view === "salary_update" && (
                        <div className="mt-1 text-[11px] text-slate-500">
                          Cash ${l.old_cash_amount || 0} → ${l.new_cash_amount || 0} · Check ${l.old_check_amount || 0} → ${l.new_check_amount || 0} · <span className="font-semibold text-emerald-700">Total ${l.old_daily_rate || 0} → ${l.new_daily_rate || 0}</span>
                        </div>
                      )}
                      {l.view === "ai_search" && (
                        <div className="mt-1 max-w-[640px]" data-testid={`ai-question-${l.id}`}>
                          <div className="text-[12px] text-slate-800 font-medium italic">
                            &ldquo;{l.question}&rdquo;
                          </div>
                          {l.answer_preview && (
                            <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">
                              → {l.answer_preview}
                            </div>
                          )}
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {l.rows_considered || 0} rows scanned
                          </div>
                        </div>
                      )}
                      {l.feature === "atlas" && (
                        <AtlasLogDetail row={l} />
                      )}
                    </Td>
                    <Td className="text-right">
                      <div className="text-sm text-slate-700">{formatRelativeTime(l.created_at)}</div>
                      <div className="text-xs text-slate-400">{l.created_at ? new Date(l.created_at).toLocaleString() : ""}</div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AtlasLogDetail({ row }) {
  const a = row.action || "";
  const text = row.text;
  if (a === "task_create") {
    return (
      <div className="mt-1 text-[12px] text-slate-700">
        <span className="text-slate-400">Added task to </span>
        <span className="font-semibold uppercase text-[10px] tracking-widest text-slate-500">{row.column_key || "?"}</span>
        {text && <div className="text-slate-800 italic">&ldquo;{text}&rdquo;</div>}
      </div>
    );
  }
  if (a === "task_update") {
    const changes = row.changes || {};
    return (
      <div className="mt-1 text-[12px] text-slate-700">
        {text && <div className="text-slate-800 italic">&ldquo;{text}&rdquo;</div>}
        <div className="text-[11px] text-slate-500 space-x-2 mt-0.5">
          {changes.text && <span>text: <span className="line-through text-slate-400">{changes.text[0]}</span> → <span className="text-slate-700">{changes.text[1]}</span></span>}
          {changes.urgency && <span>urgency: <span className="text-slate-400">{changes.urgency[0]}</span> → <span className="text-slate-700">{changes.urgency[1]}</span></span>}
          {changes.is_done !== undefined && <span>done: <span className="text-slate-700">{String(changes.is_done[1])}</span></span>}
        </div>
      </div>
    );
  }
  if (a === "task_move") {
    return (
      <div className="mt-1 text-[12px] text-slate-700">
        {text && <div className="text-slate-800 italic">&ldquo;{text}&rdquo;</div>}
        <div className="text-[11px] text-slate-500">
          from <span className="font-semibold uppercase tracking-widest">{row.from}</span> → <span className="font-semibold uppercase tracking-widest">{row.to}</span> (order {row.order})
        </div>
      </div>
    );
  }
  if (a === "task_delete") {
    return (
      <div className="mt-1 text-[12px] text-rose-700">
        <span className="text-rose-400">Trashed from </span>
        <span className="font-semibold uppercase text-[10px] tracking-widest text-rose-500">{row.column_key || "?"}</span>
        {text && <div className="text-slate-800 italic line-through decoration-rose-400/70">&ldquo;{text}&rdquo;</div>}
      </div>
    );
  }
  if (a === "task_restore") {
    return (
      <div className="mt-1 text-[12px] text-emerald-700">
        <span className="text-emerald-500">Restored to </span>
        <span className="font-semibold uppercase text-[10px] tracking-widest text-emerald-600">{row.column_key || "?"}</span>
        {text && <div className="text-slate-800 italic">&ldquo;{text}&rdquo;</div>}
      </div>
    );
  }
  if (a === "column_rename") {
    return (
      <div className="mt-1 text-[12px] text-slate-700">
        Column <span className="font-semibold uppercase tracking-widest text-[10px]">{row.column_key}</span>:
        <span className="ml-1 line-through text-slate-400">{row.old_name}</span>
        <span className="mx-1">→</span>
        <span className="text-slate-800">{row.new_name}</span>
      </div>
    );
  }
  if (a === "group_rename") {
    return (
      <div className="mt-1 text-[12px] text-slate-700">
        Group <span className="font-semibold uppercase tracking-widest text-[10px]">{row.group}</span>:
        <span className="ml-1 line-through text-slate-400">{row.old_name}</span>
        <span className="mx-1">→</span>
        <span className="text-slate-800">{row.new_name}</span>
      </div>
    );
  }
  if (a === "layout_update") {
    const patch = row.layout || {};
    const parts = [];
    if (patch.top_panels) parts.push("top: " + patch.top_panels.join(" · "));
    if (patch.team_order) parts.push("team: " + patch.team_order.join(" · "));
    if (patch.office_order) parts.push("office: " + patch.office_order.join(" · "));
    if (patch.office_middle_order) parts.push("mid: " + patch.office_middle_order.join(" · "));
    if (patch.team_orientation) parts.push("team stack: " + patch.team_orientation);
    if (patch.office_middle_orientation) parts.push("mid stack: " + patch.office_middle_orientation);
    return (
      <div className="mt-1 text-[11px] text-slate-500">
        {parts.join(" ; ") || "layout changed"}
      </div>
    );
  }
  return null;
}


function Th({ children, className = "" }) {
  return (
    <th className={`px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={`px-5 py-3 ${className}`}>{children}</td>;
}

function PermToggle({ label, desc, checked, onChange, disabled, note, noteTone, testId }) {
  const noteCls = noteTone === "danger" ? "text-rose-600 font-semibold" : "text-amber-700";
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border ${
      disabled ? "border-slate-100 bg-slate-50/60 opacity-60 cursor-not-allowed"
               : "border-slate-200 hover:bg-slate-50 cursor-pointer"
    }`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-0.5 h-4 w-4 accent-[#327de3]"
        data-testid={testId}
      />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        <div className="text-xs text-slate-500">{desc}</div>
        {note && <div className={`mt-0.5 text-[11px] ${noteCls}`}>{note}</div>}
      </div>
    </label>
  );
}

/* =================== SALARIES PANEL (super admin only) =================== */
function SalariesPanel() {
  const [unlockToken, setUnlockToken] = useState(null);
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({}); // emp -> { hourly_rate, daily_rate }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Apply-changes modal (shows after Save is clicked)
  const [applyOpen, setApplyOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  // Edit-lock: by default, the fields are visually locked even AFTER the
  // page-level password unlock. A separate "Unlock to edit" action is required.
  const [editUnlocked, setEditUnlocked] = useState(false);
  const [editUnlockOpen, setEditUnlockOpen] = useState(false);
  const [editPassword, setEditPassword] = useState("");
  const [editUnlocking, setEditUnlocking] = useState(false);
  // Salary file uploads
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const fileCardRef = useRef(null);
  const [fileCardH, setFileCardH] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => { if (fileCardRef.current) setFileCardH(fileCardRef.current.offsetHeight); }, 50);
    return () => clearTimeout(t);
  }, [unlockToken, files.length, filesLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/salaries");
      const list = Array.isArray(data) ? data : (data?.rows || []);
      setRows(list);
      const init = {};
      list.forEach((r) => {
        init[r.employee] = {
          cash_amount: r.cash_amount || 0,
          check_amount: r.check_amount || 0,
          add_pay_amount: r.add_pay_amount || 0,
          additional_note: r.additional_note || "",
          tax_wh_amount: r.tax_wh_amount || 0,
          emp_type: r.emp_type || "",
          notes: r.notes || "",
          group: r.group || "",
        };
      });
      setDraft(init);
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    setFilesLoading(true);
    try {
      const { data } = await api.get("/admin/salaries/files");
      setFiles(data || []);
    } catch (e) {
      // soft-fail
      console.warn("loadFiles failed", e?.message);
    } finally {
      setFilesLoading(false);
    }
  };

  useEffect(() => { load(); loadFiles(); }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/salaries/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      await loadFiles();
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (f) => {
    try {
      const resp = await api.get(`/admin/salaries/files/${f.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: f.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename || `salaries-${f.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    }
  };

  const doUnlock = async () => {
    if (!password) return;
    setUnlocking(true);
    try {
      const { data } = await api.post("/admin/salaries/unlock", { password });
      setUnlockToken(data.unlock_token);
      setPassword("");
      setEditUnlocked(false); // Fields stay locked until "Unlock" is pressed again
      toast.success("Salaries unlocked for viewing");
    } catch (e) {
      { const _m = apiErrorMessage(e, "Invalid password"); if (_m) toast.error(_m); }
    } finally {
      setUnlocking(false);
    }
  };

  // Separate password gate to ENABLE editing the fields. Fields are greyed out
  // by default after page unlock; pressing "Unlock" opens this modal.
  const doEditUnlock = async () => {
    if (!editPassword) return;
    setEditUnlocking(true);
    try {
      const { data } = await api.post("/admin/salaries/unlock", { password: editPassword });
      setUnlockToken(data.unlock_token); // refresh token
      setEditUnlocked(true);
      setEditPassword("");
      setEditUnlockOpen(false);
      toast.success("Fields unlocked for editing");
    } catch (e) {
      { const _m = apiErrorMessage(e, "Invalid password"); if (_m) toast.error(_m); }
    } finally {
      setEditUnlocking(false);
    }
  };

  const lock = () => { setUnlockToken(null); setEditUnlocked(false); toast.info("Salaries locked"); };
  const lockEdit = () => { setEditUnlocked(false); toast.info("Edit mode locked"); };

  const setField = (emp, field, val) => {
    setDraft((d) => ({ ...d, [emp]: { ...d[emp], [field]: ["additional_note", "emp_type", "notes", "group"].includes(field) ? val : Math.max(0, Number(val) || 0) } }));
  };

  const save = async () => {
    if (!unlockToken) {
      toast.error("Unlock with password first");
      return;
    }
    // Determine what changed: per-employee, separate cash & check
    const changes = [];
    rows.forEach((r) => {
      const d = draft[r.employee] || { cash_amount: 0, check_amount: 0 };
      const newCash = Number(d.cash_amount) || 0;
      const newCheck = Number(d.check_amount) || 0;
      const prevCash = Number(r.cash_amount) || 0;
      const prevCheck = Number(r.check_amount) || 0;
      const cashChanged = newCash !== prevCash;
      const checkChanged = newCheck !== prevCheck;
      const newAP = Number(d.add_pay_amount) || 0;
      const prevAP = Number(r.add_pay_amount) || 0;
      const apChanged = newAP !== prevAP;
      const newTW = Number(d.tax_wh_amount) || 0;
      const prevTW = Number(r.tax_wh_amount) || 0;
      const twChanged = newTW !== prevTW;
      const newTy = (d.emp_type || "").trim();
      const prevTy = r.emp_type || "";
      const tyChanged = newTy !== prevTy;
      if (cashChanged || checkChanged || apChanged || twChanged || tyChanged) {
        changes.push({
          employee: r.employee,
          cash_amount: newCash,
          check_amount: newCheck,
          prev_cash: prevCash,
          prev_check: prevCheck,
          cash_changed: cashChanged,
          check_changed: checkChanged,
          add_pay_amount: newAP,
          prev_add_pay: prevAP,
          add_pay_changed: apChanged,
          additional_note: d.additional_note || "",
          tax_wh_amount: newTW,
          prev_tax_wh: prevTW,
          tax_wh_changed: twChanged,
          emp_type: newTy,
          prev_emp_type: prevTy,
          emp_type_changed: tyChanged,
        });
      }
    });
    const addChanges = additionalChanges();
    if (changes.length === 0 && addChanges.length === 0) {
      toast.info("No changes to save");
      return;
    }
    if (changes.length === 0) {
      commitSave([]);
      return;
    }
    setPendingChanges(changes);
    setApplyOpen(true);
  };

  // Text-only edits (notes, section) save directly; amount/Type changes go through the effective-date prompt.
  const additionalChanges = () => rows.filter((r) => {
    const d = draft[r.employee] || {};
    return (d.additional_note || "") !== (r.additional_note || "") || (d.notes || "") !== (r.notes || "") || (d.group || "") !== (r.group || "");
  }).map((r) => ({ employee: r.employee, additional_note: draft[r.employee].additional_note || "", notes: draft[r.employee].notes || "",
    group: draft[r.employee].group || "", cash_amount: Number(r.cash_amount) || 0, check_amount: Number(r.check_amount) || 0 }));

  const commitSave = async (baseItems) => {
    const byEmp = {};
    baseItems.forEach((it) => { byEmp[it.employee] = it; });
    additionalChanges().forEach((a) => {
      const { cash_amount, check_amount, ...add } = a;
      byEmp[a.employee] = byEmp[a.employee] ? { ...byEmp[a.employee], ...add } : a;
    });
    const items = Object.values(byEmp);
    setSaving(true);
    try {
      const { data } = await api.put("/admin/salaries", { unlock_token: unlockToken, items });
      toast.success(`Saved · ${data.changed} updated. Fields auto-locked — press Unlock to edit again.`);
      setApplyOpen(false);
      setPendingChanges([]);
      await load();
      // Auto-lock edit mode after every successful save — re-press "Unlock"
      // (with password) to make further edits.
      setEditUnlocked(false);
    } catch (e) {
      // 423 = salary section locked (unlock_token expired) — NOT an auth
      // problem. Toast + clear the stale token so the user can re-unlock
      // without being bounced to /login.
      if (e?.response?.status === 423) {
        toast.error("Salary section locked — please re-enter your password to continue editing.");
        setUnlockToken(null);
        setEditUnlocked(false);
        setApplyOpen(false);
      } else {
        const _m = apiErrorMessage(e); if (_m) toast.error(_m);
      }
    } finally {
      setSaving(false);
    }
  };

  const locked = !unlockToken;
  const [editRow, setEditRow] = useState(null);
  const [editSnap, setEditSnap] = useState(null);
  const openEdit = (r) => { setEditSnap(draft[r.employee]); setEditRow(r); };
  const cancelEdit = () => { if (editRow) setDraft((d) => ({ ...d, [editRow.employee]: editSnap })); setEditRow(null); };
  const [archiveEmp, setArchiveEmp] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [reactivate, setReactivate] = useState(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const restoreEmp = (emp) => setReactivate(rows.find((r) => r.employee === emp) || { employee: emp });
  const unpaidNames = rows.filter((r) => r.status !== "archived" && !(r.cash_history || []).length && !(r.check_history || []).length && !weeklyGross(r)).map((r) => r.employee);
  const toggleGroup = (emp) => setField(emp, "group", (draft[emp]?.group || "") === "painters" ? "" : "painters");

  return (
    <div className="space-y-4" data-testid="salaries-panel">
      {locked ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-md">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={18} weight="bold" className="text-amber-600" />
            <h3 className="font-display text-lg font-semibold text-slate-900">Salaries are locked</h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Re-enter your super admin password to view and edit salary figures. Stays unlocked until you sign out
            (or one hour, whichever comes first).
          </p>
          <Input
            type="password"
            placeholder="Super admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") doUnlock(); }}
            className="mb-3"
            data-testid="salaries-password-input"
          />
          <Button onClick={doUnlock} disabled={unlocking || !password} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="salaries-unlock-btn">
            {unlocking ? <CircleNotch size={16} weight="bold" className="mr-2 animate-spin" data-testid="salary-unlock-spinner" /> : <LockOpen size={16} weight="bold" className="mr-2" />} {unlocking ? "Verifying…" : "Unlock"}
          </Button>
        </div>
      ) : (
        <>
          {/* FILE UPLOAD — physical salaries file with version history */}
          <div ref={fileCardRef}>
            <SalaryFileCard
              files={files}
              filesLoading={filesLoading}
              uploading={uploading}
              onUpload={uploadFile}
              onDownload={downloadFile}
              onOpenVersions={() => setVersionsOpen(true)}
            />
          </div>
          <SalaryNotesPanel unlockToken={unlockToken} defaultHeight={fileCardH} />

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Employee salaries</h3>
              <p className="text-xs text-slate-500">
                Enter <strong>weekly</strong> amounts. Weekly Total Gross = Check + Cash + Tax WH + Addit. Pmt/Week. Eff. Daily Rate = Weekly Total Gross ÷ 5. Weekly Take Home Pay excludes Tax WH. Fields are <strong>locked by default</strong> — press <strong>Unlock</strong> to enter your password and edit. The panel <strong>auto-locks</strong> after every save.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button size="sm" onClick={() => setAddOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="salaries-add-employee-btn">
                <UserPlus size={14} className="mr-1.5" /> Add Employee
              </Button>
              {editUnlocked ? (
                <Button variant="outline" size="sm" onClick={lockEdit} data-testid="salaries-edit-lock-btn">
                  <Lock size={14} className="mr-1.5" /> Lock fields
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditUnlockOpen(true)}
                  className="border-[#327de3] text-[#327de3] hover:bg-[#327de3]/5"
                  data-testid="salaries-edit-unlock-btn"
                >
                  <Lock size={14} className="mr-1.5" /> Unlock to edit
                </Button>
              )}
              <Button
                onClick={save}
                disabled={saving || !editUnlocked}
                className="bg-[#327de3] hover:bg-[#1f64c2] text-white disabled:opacity-50"
                data-testid="salaries-save-btn"
                title={!editUnlocked ? "Unlock fields first" : "Save salary changes"}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
          {loading ? <div className="text-sm text-slate-400 px-2">Loading…</div> : (
            <>
              <SalaryTable rows={rows.filter((r) => r.status !== "archived" && (draft[r.employee]?.group || "") !== "painters")} draft={draft} setField={setField} editUnlocked={editUnlocked}
                onEdit={openEdit} onToggleGroup={toggleGroup} onArchive={setArchiveEmp} testId="salaries-table" emptyText="No employees found in the timesheet." />
              <div className="pt-2" data-testid="salaries-painters-section">
                <h3 className="font-display text-lg font-semibold text-slate-900">Painters</h3>
                <p className="text-xs text-slate-500 mb-2">Same employees, same salary and timesheet identity — just grouped here. Use the paintbrush / person icon on a row (or Section in Edit) to move someone, then Save changes.</p>
                <SalaryTable rows={rows.filter((r) => r.status !== "archived" && (draft[r.employee]?.group || "") === "painters")} draft={draft} setField={setField} editUnlocked={editUnlocked}
                  onEdit={openEdit} onToggleGroup={toggleGroup} onArchive={setArchiveEmp} testId="salaries-painters-table" emptyText="No painters yet." mode="painters" />
              </div>
              <div className="pt-2" data-testid="salaries-archived-section">
                <button type="button" onClick={() => setArchivedOpen((v) => !v)} className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900" data-testid="salaries-archived-toggle">
                  {archivedOpen ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />} Archived ({rows.filter((r) => r.status === "archived").length})
                </button>
                {archivedOpen && <>
                  <p className="text-xs text-slate-500 mb-2">Archived employees are paid $0 from their archival date (the archive date itself is unpaid). Earlier timesheets and salary history stay in every report. Reactivate to enter new effective-dated pay.</p>
                  <SalaryTable rows={rows.filter((r) => r.status === "archived")} draft={draft} setField={setField} editUnlocked={editUnlocked}
                    onEdit={openEdit} onRestore={restoreEmp} testId="salaries-archived-table" emptyText="No archived employees." mode="archived" />
                </>}
              </div>
            </>
          )}
        </>
      )}

      <AddEmployeeDialog open={addOpen} mode="add" suggestions={unpaidNames} unlockToken={unlockToken} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />
      <AddEmployeeDialog open={!!reactivate} mode="reactivate" employee={reactivate?.employee} archivedOn={reactivate?.archived_effective_date || ""} unlockToken={unlockToken}
        onClose={() => setReactivate(null)} onDone={() => { setReactivate(null); load(); }} />
      <ArchiveEmployeeDialog employee={archiveEmp} unlockToken={unlockToken} onClose={() => setArchiveEmp(null)} onDone={() => { setArchiveEmp(null); load(); }} />
      <SalaryEditDialog row={editRow} draft={draft} setField={setField} editUnlocked={editUnlocked} saving={saving}
        onCancel={cancelEdit} onRequestUnlock={() => setEditUnlockOpen(true)} onSave={() => { setEditRow(null); save(); }} />
      <ApplySalaryChangesDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        changes={pendingChanges}
        saving={saving}
        onConfirm={commitSave}
      />

      {/* Edit-unlock password modal */}
      <Dialog open={editUnlockOpen} onOpenChange={setEditUnlockOpen}>
        <DialogContent className="sm:max-w-md" data-testid="salary-edit-unlock-modal">
          <DialogHeader>
            <DialogTitle>Unlock fields to edit</DialogTitle>
            <DialogDescription>
              Re-enter your super-admin password to enable salary edits. Fields lock again automatically after each save.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="salary-edit-password" className="text-xs text-slate-600">Super-admin password</Label>
            <Input
              id="salary-edit-password"
              type="password"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !editUnlocking) doEditUnlock(); }}
              autoFocus
              data-testid="salary-edit-password-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setEditUnlockOpen(false); setEditPassword(""); }}
              disabled={editUnlocking}
              data-testid="salary-edit-unlock-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={doEditUnlock}
              disabled={editUnlocking || !editPassword}
              className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
              data-testid="salary-edit-unlock-confirm"
            >
              {editUnlocking && <CircleNotch size={14} weight="bold" className="mr-1.5 animate-spin" />}{editUnlocking ? "Unlocking…" : "Unlock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Previous versions modal */}
      <PreviousVersionsDialog
        open={versionsOpen}
        onOpenChange={setVersionsOpen}
        files={files}
        onDownload={downloadFile}
      />
    </div>
  );
}

/* ----- Apply Salary Changes modal ----- */
function ApplySalaryChangesDialog({ open, onOpenChange, changes, saving, onConfirm }) {
  // mode: "once" (single policy for all changes) | "per" (per-employee per-kind policies)
  const [mode, setMode] = useState("once");
  // bulk policy when mode === "once"
  const [bulk, setBulk] = useState({ kind: "forward", start: "", end: "", noEnd: false });
  // per-row policies when mode === "per". Key: `${employee}|${kind}` → { kind, start, end, noEnd }
  const [perPolicy, setPerPolicy] = useState({});

  useEffect(() => {
    if (open) {
      setMode("once");
      setBulk({ kind: "forward", start: "", end: "", noEnd: false });
      // Initialize per-policy defaults
      const init = {};
      changes.forEach((c) => {
        if (c.cash_changed) init[`${c.employee}|cash`] = { kind: "forward", start: "", end: "", noEnd: false };
        if (c.check_changed) init[`${c.employee}|check`] = { kind: "forward", start: "", end: "", noEnd: false };
        if (c.add_pay_changed) init[`${c.employee}|add_pay`] = { kind: "forward", start: "", end: "", noEnd: false };
        if (c.tax_wh_changed) init[`${c.employee}|tax_wh`] = { kind: "forward", start: "", end: "", noEnd: false };
        if (c.emp_type_changed) init[`${c.employee}|emp_type`] = { kind: "forward", start: "", end: "", noEnd: false };
      });
      setPerPolicy(init);
    }
  }, [open, changes]);

  const buildItems = () => {
    const policyFromState = (p) => {
      if (p.kind === "forward") return { mode: "forward" };
      if (p.kind === "all") return { mode: "all" };
      const pol = { mode: "custom", start_date: p.start || null };
      if (!p.noEnd && p.end) pol.end_date = p.end;
      return pol;
    };
    return changes.map((c) => {
      const item = { employee: c.employee, cash_amount: c.cash_amount, check_amount: c.check_amount };
      if (c.cash_changed) {
        const src = mode === "once" ? bulk : perPolicy[`${c.employee}|cash`];
        item.cash_apply = policyFromState(src);
      }
      if (c.check_changed) {
        const src = mode === "once" ? bulk : perPolicy[`${c.employee}|check`];
        item.check_apply = policyFromState(src);
      }
      if (c.add_pay_changed) {
        const src = mode === "once" ? bulk : (perPolicy[`${c.employee}|add_pay`] || bulk);
        item.add_pay_amount = c.add_pay_amount;
        item.additional_note = c.additional_note;
        item.add_pay_apply = policyFromState(src);
      }
      if (c.tax_wh_changed) {
        item.tax_wh_amount = c.tax_wh_amount;
        item.tax_wh_apply = policyFromState(mode === "once" ? bulk : (perPolicy[`${c.employee}|tax_wh`] || bulk));
      }
      if (c.emp_type_changed) {
        item.emp_type = c.emp_type;
        item.emp_type_apply = policyFromState(mode === "once" ? bulk : (perPolicy[`${c.employee}|emp_type`] || bulk));
      }
      return item;
    });
  };

  const validate = () => {
    const valid = (p) => {
      if (p.kind !== "custom") return true;
      if (!p.start) return false;
      if (!p.noEnd && !p.end) return false;
      if (!p.noEnd && p.end && p.end < p.start) return false;
      return true;
    };
    if (mode === "once") return valid(bulk);
    return Object.values(perPolicy).every(valid);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="salary-apply-modal">
        <DialogHeader>
          <DialogTitle>How should these salary changes take effect?</DialogTitle>
          <DialogDescription>
            Existing timesheet data calculated with old rates will stay intact unless you choose
            &ldquo;Apply to all&rdquo;. This protects historical pay totals when you give raises mid-year.
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5 w-fit mb-3" data-testid="salary-apply-mode">
          <button
            onClick={() => setMode("once")}
            className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
              mode === "once" ? "bg-white shadow-sm text-[#327de3]" : "text-slate-500"
            }`}
            data-testid="salary-apply-mode-once"
          >
            Once for all ({changes.length} {changes.length === 1 ? "employee" : "employees"})
          </button>
          <button
            onClick={() => setMode("per")}
            className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
              mode === "per" ? "bg-white shadow-sm text-[#327de3]" : "text-slate-500"
            }`}
            data-testid="salary-apply-mode-per"
          >
            Per employee
          </button>
        </div>

        {mode === "once" ? (
          <PolicyEditor
            value={bulk}
            onChange={setBulk}
            testIdPrefix="salary-apply-bulk"
            label={`Applies to ${changes.length} salary change${changes.length === 1 ? "" : "s"}`}
          />
        ) : (
          <div className="space-y-3" data-testid="salary-apply-per-list">
            {changes.map((c) => (
              <div key={c.employee} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="text-sm font-semibold text-slate-900 mb-1">{c.employee}</div>
                {c.cash_changed && (
                  <PolicyEditor
                    value={perPolicy[`${c.employee}|cash`] || { kind: "forward", start: "", end: "", noEnd: false }}
                    onChange={(v) => setPerPolicy((p) => ({ ...p, [`${c.employee}|cash`]: v }))}
                    label={`Cash: $${c.prev_cash.toFixed(2)} → $${c.cash_amount.toFixed(2)}`}
                    testIdPrefix={`salary-apply-${c.employee}-cash`}
                    compact
                  />
                )}
                {c.check_changed && (
                  <PolicyEditor
                    value={perPolicy[`${c.employee}|check`] || { kind: "forward", start: "", end: "", noEnd: false }}
                    onChange={(v) => setPerPolicy((p) => ({ ...p, [`${c.employee}|check`]: v }))}
                    label={`Check: $${c.prev_check.toFixed(2)} → $${c.check_amount.toFixed(2)}`}
                    testIdPrefix={`salary-apply-${c.employee}-check`}
                    compact
                  />
                )}
                {c.add_pay_changed && (
                  <PolicyEditor
                    value={perPolicy[`${c.employee}|add_pay`] || { kind: "forward", start: "", end: "", noEnd: false }}
                    onChange={(v) => setPerPolicy((p) => ({ ...p, [`${c.employee}|add_pay`]: v }))}
                    label={`Additional Payment / Week: $${(c.prev_add_pay || 0).toFixed(2)} → $${(c.add_pay_amount || 0).toFixed(2)}`}
                    testIdPrefix={`salary-apply-${c.employee}-add_pay`}
                    compact
                  />
                )}
                {c.tax_wh_changed && (
                  <PolicyEditor
                    value={perPolicy[`${c.employee}|tax_wh`] || { kind: "forward", start: "", end: "", noEnd: false }}
                    onChange={(v) => setPerPolicy((p) => ({ ...p, [`${c.employee}|tax_wh`]: v }))}
                    label={`Tax WH / Week: $${(c.prev_tax_wh || 0).toFixed(2)} → $${(c.tax_wh_amount || 0).toFixed(2)}`}
                    testIdPrefix={`salary-apply-${c.employee}-tax_wh`}
                    compact
                  />
                )}
                {c.emp_type_changed && (
                  <PolicyEditor
                    value={perPolicy[`${c.employee}|emp_type`] || { kind: "forward", start: "", end: "", noEnd: false }}
                    onChange={(v) => setPerPolicy((p) => ({ ...p, [`${c.employee}|emp_type`]: v }))}
                    label={`Type: ${c.prev_emp_type || "(blank)"} → ${c.emp_type || "(blank)"}`}
                    testIdPrefix={`salary-apply-${c.employee}-emp_type`}
                    compact
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="salary-apply-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(buildItems())}
            disabled={saving || !validate()}
            className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
            data-testid="salary-apply-confirm"
          >
            {saving ? "Saving…" : "Confirm & save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicyEditor({ value, onChange, label, testIdPrefix, compact }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className={`${compact ? "" : "bg-slate-50 border border-slate-200 rounded-lg p-3"} mt-1`}>
      {label && <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{label}</div>}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {[
          { v: "forward", l: "Apply going forward (from today)" },
          { v: "all", l: "Apply to all (past + future)" },
          { v: "custom", l: "Custom range" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => set({ kind: opt.v })}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
              value.kind === opt.v
                ? "bg-[#327de3] text-white border-[#327de3]"
                : "bg-white text-slate-600 border-slate-200 hover:border-[#327de3]"
            }`}
            data-testid={`${testIdPrefix}-${opt.v}`}
          >
            {opt.l}
          </button>
        ))}
      </div>
      {value.kind === "custom" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Start date</Label>
            <Input
              type="date"
              value={value.start}
              onChange={(e) => set({ start: e.target.value })}
              className="h-8"
              data-testid={`${testIdPrefix}-start`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">End date</Label>
            <Input
              type="date"
              value={value.end}
              disabled={value.noEnd}
              onChange={(e) => set({ end: e.target.value })}
              className="h-8"
              data-testid={`${testIdPrefix}-end`}
            />
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 mt-1">
              <input
                type="checkbox"
                checked={!!value.noEnd}
                onChange={(e) => set({ noEnd: e.target.checked, end: e.target.checked ? "" : value.end })}
                className="h-3.5 w-3.5 accent-[#327de3]"
                data-testid={`${testIdPrefix}-noend`}
              />
              No end date (open-ended)
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----- Salary file upload card ----- */
function SalaryFileCard({ files, filesLoading, uploading, onUpload, onDownload, onOpenVersions }) {
  const fileInputRef = React.useRef(null);
  const latest = files && files.length > 0 ? files[0] : null;
  const hasPrev = files && files.length > 1;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="salary-file-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-semibold text-slate-900">Recent salaries file</h3>
          {latest ? (
            <div className="mt-1">
              <button
                onClick={() => onDownload(latest)}
                className="text-sm text-[#327de3] hover:text-[#1f64c2] font-medium underline-offset-2 hover:underline truncate inline-block max-w-full"
                data-testid="salary-file-latest-name"
              >
                {latest.original_filename}
              </button>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Last updated <strong>{new Date(latest.uploaded_at).toLocaleString()}</strong>
                {latest.uploaded_by && <> by <strong>{latest.uploaded_by}</strong></>}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-500 mt-1">
              No salaries file uploaded yet. Upload the most recent physical file (PDF, Excel, etc).
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            data-testid="salary-file-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
            data-testid="salary-file-upload-btn"
          >
            {uploading ? "Uploading…" : (latest ? "Upload new version" : "Upload file")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenVersions}
            disabled={!hasPrev}
            data-testid="salary-file-versions-btn"
            title={hasPrev ? "View previous versions" : "No previous versions yet"}
          >
            Previous versions {hasPrev ? `(${files.length - 1})` : ""}
          </Button>
        </div>
      </div>
      {filesLoading && <div className="text-[11px] text-slate-400 mt-2">Loading…</div>}
    </div>
  );
}

/* ----- Previous versions dialog ----- */
function PreviousVersionsDialog({ open, onOpenChange, files, onDownload }) {
  const prev = (files || []).slice(1); // skip the latest, which is shown on the card
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto" data-testid="salary-versions-modal">
        <DialogHeader>
          <DialogTitle>Previous salaries file versions</DialogTitle>
        </DialogHeader>
        {prev.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No previous versions.</p>
        ) : (
          <ul className="space-y-2" data-testid="salary-versions-list">
            {prev.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 bg-slate-50/40"
                data-testid={`salary-version-${f.id}`}
              >
                <div className="min-w-0 flex-1">
                  <button
                    onClick={() => onDownload(f)}
                    className="text-sm text-[#327de3] hover:text-[#1f64c2] font-medium truncate inline-block max-w-full text-left"
                  >
                    {f.original_filename}
                  </button>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Uploaded <strong>{new Date(f.uploaded_at).toLocaleString()}</strong>
                    {f.uploaded_by && <> by <strong>{f.uploaded_by}</strong></>}
                    {f.size && <> · {(f.size / 1024).toFixed(1)} KB</>}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDownload(f)}
                  data-testid={`salary-version-download-${f.id}`}
                >
                  Download
                </Button>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="salary-versions-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

